import axios from "axios";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import OtpInput from "../components/OtpInput";
import { API_BASE_URL } from "../config";
import useTitle from "../hooks/useTitle";
import { toast } from "react-toastify";

export default function Register() {
  useTitle("Register | Astro Portal");
  const [form, setForm] = useState({
    profile: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    password_confirmation: "",
  });

  const [errors, setErrors] = useState({});
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(true);
  const [userEmail, setUserEmail] = useState(null);
  const [userType, setUserType] = useState("user");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpExpiry, setOtpExpiry] = useState(null);
  const navigate = useNavigate();

  // ✅ Validation
  const validateForm = () => {
    let newErrors = {};
    if (!form.first_name) newErrors.first_name = "First name is required!";
    if (!form.last_name) newErrors.last_name = "Last name is required!";
    if (!form.email) newErrors.email = "Email is required!";
    else if (!/^\S+@\S+\.\S+$/.test(form.email))
      newErrors.email = "Enter a valid email";
    if (!form.phone) newErrors.phone = "Phone number is required!";
    else if (!/^\d{10}$/.test(form.phone))
      newErrors.phone = "Enter 10 digit phone number";
    if (!form.password || form.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";
    if (!form.password_confirmation || form.password_confirmation.length < 6)
      newErrors.password_confirmation =
        "Password must be at least 6 characters";
    if (form.password !== form.password_confirmation)
      newErrors.password_confirmation = "Passwords do not match";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // ----------------- REGISTER -----------------
  const handleRegister = async () => {
    if (!validateForm()) return;

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/signup`,
        {
          type: "user",
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone_number: form.phone_number,
          password: form.password,
          password_confirmation: form.password_confirmation,
        }
      );

      if (!data.status) {
        toast.error(data.message || "Signup failed!");
        return;
      }

      // ✅ Save temporary data for OTP verification
      setUserEmail(form.email);
      setUserType("user");
      setOtpSent(true);

      // ✅ Use login_otp_expires_at from signup response
      if (data.data.login_otp_expires_at) {
        const expiryTime = new Date(data.data.login_otp_expires_at).getTime();
        setOtpExpiry(expiryTime);
      }
      toast.success(data.message || "OTP sent to your email/phone!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Signup failed!");
    }
  };

  // ----------------- VERIFY SIGNUP OTP -----------------
  const handleSignupOtpVerify = async () => {
    if (!otp.trim()) {
      toast.warning("Please enter OTP!");
      return;
    }

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/auth/verify-signup-otp`,
        {
          type: userType,
          email: userEmail,
          otp: otp,
        }
      );

      if (!data.status) {
        toast.error(data.message || "Invalid OTP!");
        return;
      }

      // ✅ Save session with JWT
      const sessionData = {
        user_id: data.data.id,
        role: "user",
        jwt_token: data.access_token,
        token_type: data.token_type,
        token_expires_at: data.expires_at || null,
        // otp_expires_at: otpExpiry ? new Date(otpExpiry).toISOString() : null,
        user: data.data,
      };

      sessionStorage.clear();
      sessionStorage.setItem("authSession", JSON.stringify(sessionData));
      toast.success(data.message || "OTP verified!");
      setShowRegister(false);

      setTimeout(() => {
        navigate("/"); // after signup → verified → dashboard
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.message || "OTP verification failed!");
    }
  };

  // ----------------- OTP Countdown -----------------
  const [otpTimeLeft, setOtpTimeLeft] = useState(0);

  useEffect(() => {
    if (!otpExpiry) return;

    const interval = setInterval(() => {
      const remaining = otpExpiry - Date.now();
      if (remaining <= 0) {
        setOtpTimeLeft(0);
        clearInterval(interval);
      } else {
        setOtpTimeLeft(Math.floor(remaining / 1000));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [otpExpiry]);

  // ----------------- Session Expiry Check -----------------
  useEffect(() => {
    const interval = setInterval(() => {
      const session = JSON.parse(sessionStorage.getItem("authSession"));
      if (
        session &&
        session.token_expires_at &&
        new Date(session.token_expires_at).getTime() <= Date.now()
      ) {
        sessionStorage.removeItem("authSession");
        toast.error("Session expired. Please login again.");
        navigate("/login");
      }
    }, 60000); // check every 1 minute

    return () => clearInterval(interval);
  }, [navigate]);

  return (
    <>
      {/* Register Form */}
      {showRegister && (
        <div className="d-flex justify-content-center align-items-center bg-light py-5">
          <div
            className="row border-0 rounded-5 shadow-lg p-4"
            style={{
              maxWidth: "1200px",
              background: "linear-gradient(145deg, #ffffff, #f0f0f0)",
              boxShadow:
                "10px 10px 25px rgba(0,0,0,0.15), -10px -10px 25px rgba(255,255,255,0.8)",
              transform: "translateY(0)",
              transition: "all 0.3s ease-in-out",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-8px) scale(1.01)";
              e.currentTarget.style.boxShadow =
                "15px 15px 35px rgba(0,0,0,0.2), -15px -15px 35px rgba(255,255,255,0.9)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0) scale(1)";
              e.currentTarget.style.boxShadow =
                "10px 10px 25px rgba(0,0,0,0.15), -10px -10px 25px rgba(255,255,255,0.8)";
            }}
          >
            <motion.div
              initial={{ x: -80, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="col-md-6 px-4 py-0 d-flex flex-column justify-content-center"
            >
              <h3 className="mb-4 text-center fw-bold text-gradient">
                Create Your Account
              </h3>
              {!otpSent ? (
                <>
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-secondary">
                      Profile Photo *
                    </label>
                    <motion.input
                      whileFocus={{ scale: 1.02 }}
                      type="file"
                      className="form-control rounded-pill shadow-sm"
                      style={{
                        boxShadow:
                          "inset 4px 4px 8px #c9c9c9, inset -4px -4px 8px #ffffff",
                      }}
                      onChange={(e) =>
                        setForm({ ...form, profile: e.target.value })
                      }
                    />
                  </div>

                  <div className="row">
                    {/* First Name */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold text-secondary">
                        First Name *
                      </label>
                      <motion.input
                        whileFocus={{ scale: 1.02 }}
                        type="text"
                        placeholder="Enter your first name"
                        className={`form-control rounded-pill shadow-sm ${
                          errors.first_name ? "is-invalid" : ""
                        }`}
                        style={{
                          boxShadow:
                            "inset 4px 4px 8px #c9c9c9, inset -4px -4px 8px #ffffff",
                        }}
                        onChange={(e) =>
                          setForm({ ...form, first_name: e.target.value })
                        }
                      />
                      {errors.first_name && (
                        <div className="invalid-feedback">
                          {errors.first_name}
                        </div>
                      )}
                    </div>

                    {/* Last Name */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold text-secondary">
                        Last Name *
                      </label>
                      <motion.input
                        whileFocus={{ scale: 1.02 }}
                        type="text"
                        placeholder="Enter your last name"
                        className={`form-control rounded-pill shadow-sm ${
                          errors.last_name ? "is-invalid" : ""
                        }`}
                        style={{
                          boxShadow:
                            "inset 4px 4px 8px #c9c9c9, inset -4px -4px 8px #ffffff",
                        }}
                        onChange={(e) =>
                          setForm({ ...form, last_name: e.target.value })
                        }
                      />
                      {errors.last_name && (
                        <div className="invalid-feedback">
                          {errors.last_name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-secondary">
                      Email Address *
                    </label>
                    <motion.input
                      whileFocus={{ scale: 1.02 }}
                      type="email"
                      placeholder="Enter your email address"
                      className={`form-control rounded-pill shadow-sm ${
                        errors.email ? "is-invalid" : ""
                      }`}
                      style={{
                        boxShadow:
                          "inset 4px 4px 8px #c9c9c9, inset -4px -4px 8px #ffffff",
                      }}
                      onChange={(e) =>
                        setForm({ ...form, email: e.target.value })
                      }
                    />
                    {errors.email && (
                      <div className="invalid-feedback">{errors.email}</div>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-secondary">
                      Phone Number *
                    </label>
                    <motion.input
                      whileFocus={{ scale: 1.02 }}
                      type="text"
                      placeholder="Enter your phone number"
                      className={`form-control rounded-pill shadow-sm ${
                        errors.phone ? "is-invalid" : ""
                      }`}
                      style={{
                        boxShadow:
                          "inset 4px 4px 8px #c9c9c9, inset -4px -4px 8px #ffffff",
                      }}
                      onChange={(e) =>
                        setForm({ ...form, phone: e.target.value })
                      }
                    />
                    {errors.phone && (
                      <div className="invalid-feedback">{errors.phone}</div>
                    )}
                  </div>

                  {/* Password */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold text-secondary">
                      Password *
                    </label>
                    <motion.input
                      whileFocus={{ scale: 1.02 }}
                      type="password"
                      placeholder="Enter your password"
                      className={`form-control rounded-pill shadow-sm ${
                        errors.password ? "is-invalid" : ""
                      }`}
                      style={{
                        boxShadow:
                          "inset 4px 4px 8px #c9c9c9, inset -4px -4px 8px #ffffff",
                      }}
                      onChange={(e) =>
                        setForm({ ...form, password: e.target.value })
                      }
                    />
                    {errors.password && (
                      <div className="invalid-feedback">{errors.password}</div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold text-secondary">
                      Confirm Password *
                    </label>
                    <motion.input
                      whileFocus={{ scale: 1.02 }}
                      type="password"
                      placeholder="Confirm your password"
                      className={`form-control rounded-pill shadow-sm ${
                        errors.password_confirmation ? "is-invalid" : ""
                      }`}
                      style={{
                        boxShadow:
                          "inset 4px 4px 8px #c9c9c9, inset -4px -4px 8px #ffffff",
                      }}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          password_confirmation: e.target.value,
                        })
                      }
                    />
                    {errors.password_confirmation && (
                      <div className="invalid-feedback">
                        {errors.password_confirmation}
                      </div>
                    )}
                  </div>

                  {/* Register Button */}
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95, y: 2 }}
                    onClick={handleRegister}
                    className="btn btn-lg w-100 fw-bold rounded-pill"
                    style={{
                      background: "linear-gradient(145deg, #4facfe, #00f2fe)",
                      color: "white",
                      boxShadow: "6px 6px 12px #c9c9c9, -6px -6px 12px #ffffff",
                    }}
                  >
                    Register
                  </motion.button>

                  <p className="text-center bg-white pt-2">
                    Already have an account? <a href="/login">Login</a>
                  </p>
                </>
              ) : (
                <OtpInput
                  otp={otp}
                  setOtp={setOtp}
                  onVerify={handleSignupOtpVerify}
                  otpTimeLeft={otpTimeLeft} // implement resend function
                />
              )}
            </motion.div>
            {/* Right Side - Image with 3D hover */}
            <motion.div
              initial={{ x: 80, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              whileHover={{ scale: 1.02, rotateY: 5 }}
              className="col-md-6 d-none d-md-flex bg-primary text-white flex-column justify-content-center align-items-center p-5"
              style={{ perspective: "1000px" }}
            >
              <h3 className="fw-bold mb-3 text-center">Welcome to Our Store</h3>
              <p className="text-center mb-4">
                Join us and enjoy exclusive deals, quick checkout, and a
                personalized shopping experience.
              </p>
              <motion.img
                src="https://cdn-icons-png.flaticon.com/512/869/869636.png"
                alt="E-commerce illustration"
                className="img-fluid"
                style={{ maxWidth: "200px" }}
                animate={{ y: [0, -8, 0] }}
                transition={{
                  repeat: Infinity,
                  duration: 3,
                  ease: "easeInOut",
                }}
              />
            </motion.div>
          </div>
        </div>
      )}
    </>
  );
}
